import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ClipboardList, Search, Filter, ChevronLeft, ChevronRight,
  Info, Download, Wifi, WifiOff, Users, ShoppingCart,
  Star, Settings, Activity, Clock
} from 'lucide-react'
import activityLogsService from '@/services/activityLogsService'
import { usersService } from '@/services/usersService'
import { useDebounce } from '@/hooks/useDebounce'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'

// ─── Constants ───────────────────────────────────────────────────────────────

const ACTION_LABELS = {
  // Auth
  login:                     'تسجيل دخول',
  register:                  'تسجيل حساب',
  logout:                    'تسجيل خروج',
  verify_email:              'تفعيل البريد',
  forgot_password:           'نسيان كلمة المرور',
  reset_password:            'إعادة تعيين كلمة المرور',
  oauth_login:               'دخول عبر OAuth',
  // Admin
  activate_user:             'تفعيل مستخدم',
  suspend_user:              'إيقاف مستخدم',
  bulk_activate_users:       'تفعيل مستخدمين (مجمّع)',
  bulk_suspend_users:        'إيقاف مستخدمين (مجمّع)',
  approve_vendor:            'قبول بائع',
  reject_vendor:             'رفض بائع',
  suspend_vendor:            'إيقاف بائع',
  update_commission:         'تعديل العمولة',
  approve_product:           'قبول منتج',
  reject_product:            'رفض منتج',
  update_loyalty_settings:   'تعديل إعدادات النقاط',
  add_loyalty_points:        'إضافة نقاط',
  deduct_loyalty_points:     'خصم نقاط',
  expire_loyalty_points:     'انتهاء صلاحية نقاط',
  update_platform_settings:  'تعديل إعدادات المنصة',
  create_banner:             'إنشاء بانر',
  update_banner:             'تحديث بانر',
  delete_banner:             'حذف بانر',
  toggle_banner:             'تفعيل/إلغاء بانر',
  reorder_banners:           'إعادة ترتيب البانرات',
  approve_return:            ' قبول مرتجع',
  process_refund:            'معالجة استرداد',
  // Customer/Vendor
  create_order:              'إنشاء طلب',
  cancel_order:              'إلغاء طلب',
  update_order_status:       'تحديث حالة الطلب',
  update_order_item_status:  'تحديث حالة عنصر الطلب',
  request_return:            'طلب مرتجع',
  add_to_cart:               'إضافة للسلة',
  update_cart:               'تحديث السلة',
  remove_from_cart:          'إزالة من السلة',
  clear_cart:                'تفريغ السلة',
  create_review:             'كتابة مراجعة',
  update_review:             'تحديث مراجعة',
  delete_review:             'حذف مراجعة',
  approve_review:            'قبول مراجعة',
  reject_review:             'رفض مراجعة',
  create_product:            'إنشاء منتج',
  update_product:            'تحديث منتج',
  delete_product:            'حذف منتج',
  update_store_profile:      'تحديث ملف المتجر',
  add_to_wishlist:           'إضافة للمفضلة',
  remove_from_wishlist:      'إزالة من المفضلة',
}

const ROLE_LABELS = {
  admin:    'مدير',
  vendor:   'بائع',
  customer: 'عميل',
  system:   'نظام',
}

const CATEGORY_LABELS = {
  auth:     'تسجيل دخول',
  order:    'طلبات',
  product:  'منتجات',
  cart:     'سلة',
  review:   'مراجعات',
  vendor:   'بائعين',
  wishlist: 'المفضلة',
  settings: 'إعدادات',
  loyalty:  'نقاط',
  system:   'نظام',
}

const ROLE_COLORS = {
  admin:    { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
  vendor:   { bg: 'rgba(245,158,11,0.1)',   color: '#d97706' },
  customer: { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  system:   { bg: 'rgba(118,118,131,0.08)', color: '#767683' },
}

const ACTION_COLORS = {
  // Positive
  login:                     { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  register:                  { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  verify_email:              { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  approve_vendor:            { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  approve_product:           { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  approve_review:            { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  activate_user:             { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  create_order:              { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  add_to_cart:               { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  create_review:             { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  add_to_wishlist:           { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  add_loyalty_points:        { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  // Negative
  logout:                    { bg: 'rgba(118,118,131,0.08)', color: '#767683' },
  suspend_user:              { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  reject_vendor:             { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  suspend_vendor:            { bg: 'rgba(245,158,11,0.1)',   color: '#d97706' },
  reject_product:            { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  reject_review:             { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  cancel_order:              { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  delete_product:            { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  delete_review:             { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  deduct_loyalty_points:     { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  remove_from_cart:          { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  remove_from_wishlist:      { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  // Info
  update_platform_settings:  { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
  update_loyalty_settings:   { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
  update_commission:         { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
  update_order_status:       { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
  update_product:            { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
  update_store_profile:      { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
  process_refund:            { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
  create_banner:             { bg: 'rgba(27,109,36,0.08)',   color: '#1B6D24' },
  update_banner:             { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
  delete_banner:             { bg: 'rgba(220,38,38,0.08)',   color: '#dc2626' },
  toggle_banner:             { bg: 'rgba(245,158,11,0.1)',   color: '#d97706' },
  reorder_banners:           { bg: 'rgba(99,102,241,0.08)',  color: '#6366f1' },
}

const ENTITY_LABELS = {
  user:       'مستخدم',
  vendor:     'بائع',
  product:    'منتج',
  order:      'طلب',
  order_item: 'عنصر طلب',
  review:     'مراجعة',
  settings:   'إعدادات',
  refund:     'استرداد',
  cart_item:  'عنصر سلة',
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDateAr(dateStr) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleString('ar-EG', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'الآن'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `منذ ${minutes} دقيقة`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `منذ ${hours} ساعة`
  const days = Math.floor(hours / 24)
  return `منذ ${days} يوم`
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div
      className="rounded-2xl p-4 flex items-center gap-3"
      style={{ background: '#fff', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: color.bg }}
      >
        <Icon size={18} style={{ color: color.color }} />
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  )
}

function RoleBadge({ role }) {
  const label = ROLE_LABELS[role] || role
  const style = ROLE_COLORS[role] || ROLE_COLORS.system
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: style.bg, color: style.color }}
    >
      {label}
    </span>
  )
}

function ActionBadge({ action }) {
  const label = ACTION_LABELS[action] || action
  const style = ACTION_COLORS[action] || { bg: 'rgba(118,118,131,0.08)', color: '#767683' }
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: style.bg, color: style.color }}
    >
      {label}
    </span>
  )
}

function MetadataPreview({ metadata }) {
  const [open, setOpen] = useState(false)
  if (!metadata || Object.keys(metadata).length === 0) return <span className="text-gray-400 text-xs">—</span>
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
      >
        <Info size={12} />
        تفاصيل
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 p-3 rounded-xl shadow-xl text-xs font-mono text-gray-700 whitespace-pre-wrap max-w-xs overflow-auto max-h-48"
          style={{ background: '#fff', border: '1px solid #e5e7eb', right: 0, minWidth: 200 }}
        >
          {JSON.stringify(metadata, null, 2)}
        </div>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ActivityLogsPage() {
  const [search,         setSearch]         = useState('')
  const [actionFilter,   setActionFilter]   = useState('')
  const [entityType,     setEntityType]     = useState('')
  const [roleFilter,     setRoleFilter]     = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [userId,         setUserId]         = useState('')
  const [from,           setFrom]           = useState('')
  const [to,             setTo]             = useState('')
  const [showFilters,    setShowFilters]    = useState(false)
  const [liveFeed,       setLiveFeed]       = useState([])
  const [isLive,         setIsLive]         = useState(false)
  const eventSourceRef = useRef(null)
  const liveFeedRef    = useRef(null)
  const { page, limit, setPage } = usePagination(1, 20)
  const debouncedSearch = useDebounce(search, 400)

  // ─── Stats ────────────────────────────────────────────────────────────────
  const { data: stats } = useQuery({
    queryKey: ['activity-log-stats'],
    queryFn: () => activityLogsService.getStats(24),
    staleTime: 30000,
    refetchInterval: 60000,
  })

  // ─── Load distinct actions ────────────────────────────────────────────────
  const { data: actions = [] } = useQuery({
    queryKey: ['activity-log-actions'],
    queryFn: activityLogsService.getDistinctActions,
    staleTime: 60000,
  })

  // ─── Load admins list ─────────────────────────────────────────────────────
  const { data: adminsData } = useQuery({
    queryKey: ['users', { role: 'admin', limit: 50 }],
    queryFn: () => usersService.getAll({ role: 'admin', limit: 50 }),
    staleTime: 60000,
  })
  const admins = adminsData?.data || []

  // ─── Main query ───────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useQuery({
    queryKey: ['activity-logs', { action: actionFilter, entityType, role: roleFilter, category: categoryFilter, userId, from, to, page, limit, search: debouncedSearch }],
    queryFn: () => activityLogsService.getLogs({
      action:     actionFilter   || undefined,
      entityType: entityType     || undefined,
      role:       roleFilter     || undefined,
      category:   categoryFilter || undefined,
      userId:     userId         || undefined,
      from:       from           || undefined,
      to:         to             || undefined,
      search:     debouncedSearch || undefined,
      page,
      limit,
    }),
  })

  const logs = data?.data?.logs || []
  const meta = data?.data?.meta || {}

  // ─── SSE Live Feed ────────────────────────────────────────────────────────
  const toggleLiveFeed = useCallback(() => {
    if (isLive) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      setIsLive(false)
      return
    }

    try {
      const es = activityLogsService.createEventSource()
      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'connected') return
          setLiveFeed(prev => [data, ...prev].slice(0, 100))
        } catch {}
      }
      es.onerror = () => {
        es.close()
        setIsLive(false)
      }
      eventSourceRef.current = es
      setIsLive(true)
    } catch {}
  }, [isLive])

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  // ─── Filters ──────────────────────────────────────────────────────────────
  const resetFilters = () => {
    setActionFilter('')
    setEntityType('')
    setRoleFilter('')
    setCategoryFilter('')
    setUserId('')
    setFrom('')
    setTo('')
    setSearch('')
    setPage(1)
  }

  const hasFilters = actionFilter || entityType || roleFilter || categoryFilter || userId || from || to || debouncedSearch

  // ─── Export ────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    await activityLogsService.exportCsv({
      action: actionFilter || undefined,
      entityType: entityType || undefined,
      role: roleFilter || undefined,
      category: categoryFilter || undefined,
      userId: userId || undefined,
      from: from || undefined,
      to: to || undefined,
    })
  }

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(0,6,102,0.08)' }}
          >
            <ClipboardList size={20} style={{ color: '#000666' }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Cairo, sans-serif' }}>
              سجل النشاطات
            </h1>
            <p className="text-sm text-gray-500">جميع الإجراءات في المنصة — تتبع مباشر</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Live toggle */}
          <button
            onClick={toggleLiveFeed}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all"
            style={{
              background: isLive ? 'rgba(27,109,36,0.08)' : '#f3f4f6',
              color: isLive ? '#1B6D24' : '#374151',
              border: isLive ? '1px solid rgba(27,109,36,0.2)' : '1px solid transparent',
            }}
          >
            {isLive ? <Wifi size={15} /> : <WifiOff size={15} />}
            {isLive ? 'مباشر' : 'تغذية مباشرة'}
            {isLive && (
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#1B6D24' }} />
            )}
          </button>

          {/* Export */}
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all"
            style={{ background: '#f3f4f6', color: '#374151' }}
          >
            <Download size={15} />
            تصدير
          </button>

          {/* Filters toggle */}
          <button
            onClick={() => setShowFilters(f => !f)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={{
              background: showFilters ? 'rgba(0,6,102,0.08)' : '#f3f4f6',
              color: showFilters ? '#000666' : '#374151',
              border: showFilters ? '1px solid rgba(0,6,102,0.2)' : '1px solid transparent',
            }}
          >
            <Filter size={15} />
            فلترة
            {hasFilters && (
              <span className="w-2 h-2 rounded-full" style={{ background: '#FF6D00' }} />
            )}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
          <StatCard
            icon={Activity}
            label={`نشاط (${stats.hours} ساعة)`}
            value={stats.total.toLocaleString()}
            color={{ bg: 'rgba(0,6,102,0.08)', color: '#000666' }}
          />
          {stats.byRole?.map(r => (
            <StatCard
              key={r.role}
              icon={r.role === 'admin' ? Settings : r.role === 'vendor' ? ShoppingCart : Users}
              label={ROLE_LABELS[r.role] || r.role}
              value={r.count.toLocaleString()}
              color={ROLE_COLORS[r.role] || ROLE_COLORS.system}
            />
          ))}
          {stats.topActions?.slice(0, 1).map(a => (
            <StatCard
              key={a.action}
              icon={Star}
              label={ACTION_LABELS[a.action] || a.action}
              value={a.count.toLocaleString()}
              color={{ bg: 'rgba(245,158,11,0.1)', color: '#d97706' }}
            />
          ))}
        </div>
      )}

      {/* Live Feed Banner */}
      {isLive && liveFeed.length > 0 && (
        <div
          className="rounded-2xl p-4"
          style={{ background: 'rgba(27,109,36,0.04)', border: '1px solid rgba(27,109,36,0.15)' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#1B6D24' }} />
            <span className="text-sm font-semibold text-gray-700">آخر النشاطات — مباشر</span>
          </div>
          <div ref={liveFeedRef} className="space-y-2 max-h-48 overflow-y-auto">
            {liveFeed.slice(0, 10).map((item, i) => (
              <div key={item.id || i} className="flex items-center gap-3 text-xs">
                <span className="text-gray-400 w-20 flex-shrink-0">{formatRelativeTime(item.createdAt)}</span>
                <RoleBadge role={item.role} />
                <ActionBadge action={item.action} />
                {item.entityLabel && (
                  <span className="text-gray-500 truncate max-w-[200px]">{item.entityLabel}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters panel */}
      {showFilters && (
        <div
          className="rounded-2xl p-5 space-y-4"
          style={{ background: '#fff', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
        >
          {/* Search */}
          <div className="relative">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="بحث في الإجراءات..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              className="w-full pr-10 pl-4 py-2 rounded-lg text-sm border border-gray-200 bg-white focus:outline-none focus:border-blue-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {/* Role filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">الدور</label>
              <select
                value={roleFilter}
                onChange={e => { setRoleFilter(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white focus:outline-none focus:border-blue-400"
              >
                <option value="">جميع الأدوار</option>
                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {/* Category filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">الفئة</label>
              <select
                value={categoryFilter}
                onChange={e => { setCategoryFilter(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white focus:outline-none focus:border-blue-400"
              >
                <option value="">جميع الفئات</option>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {/* Action filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">الإجراء</label>
              <select
                value={actionFilter}
                onChange={e => { setActionFilter(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white focus:outline-none focus:border-blue-400"
              >
                <option value="">جميع الإجراءات</option>
                {actions.map(a => (
                  <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
                ))}
              </select>
            </div>

            {/* Entity type */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">نوع الكيان</label>
              <select
                value={entityType}
                onChange={e => { setEntityType(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white focus:outline-none focus:border-blue-400"
              >
                <option value="">جميع الأنواع</option>
                {Object.entries(ENTITY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {/* User filter (admin) */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">المدير</label>
              <select
                value={userId}
                onChange={e => { setUserId(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white focus:outline-none focus:border-blue-400"
              >
                <option value="">جميع المستخدمين</option>
                {admins.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-2 max-w-md">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">من تاريخ</label>
              <input
                type="date"
                value={from}
                onChange={e => { setFrom(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white focus:outline-none focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">إلى تاريخ</label>
              <input
                type="date"
                value={to}
                onChange={e => { setTo(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white focus:outline-none focus:border-blue-400"
              />
            </div>
          </div>

          {hasFilters && (
            <button
              onClick={resetFilters}
              className="text-xs text-red-500 hover:text-red-700 font-medium"
            >
              × مسح الفلاتر
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ background: '#fff', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          <div className="text-center py-20 text-red-500 text-sm">تعذّر تحميل السجلات</div>
        ) : logs.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="لا توجد نشاطات"
            description="لم يتم تسجيل أي إجراءات بعد أو لا تطابق الفلاتر المحددة"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">الإجراء</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">الدور</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">المستخدم</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">الفئة</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">الكيان</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">التفاصيل</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">IP</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">التاريخ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                    {/* Action */}
                    <td className="px-4 py-3.5">
                      <ActionBadge action={log.action} />
                    </td>

                    {/* Role */}
                    <td className="px-4 py-3.5">
                      <RoleBadge role={log.role} />
                    </td>

                    {/* User */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                          style={{ background: ROLE_COLORS[log.role]?.color || '#767683' }}
                        >
                          {log.user?.name?.charAt(0) || log.role?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-800">{log.user?.name || '—'}</div>
                          <div className="text-xs text-gray-400">{log.user?.email || log.role}</div>
                        </div>
                      </div>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3.5">
                      <span
                        className="inline-block text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(0,6,102,0.06)', color: '#000666' }}
                      >
                        {CATEGORY_LABELS[log.category] || log.category}
                      </span>
                    </td>

                    {/* Entity */}
                    <td className="px-4 py-3.5">
                      {log.entityType ? (
                        <div>
                          <span
                            className="inline-block text-xs font-medium px-2 py-0.5 rounded-full mb-0.5"
                            style={{ background: 'rgba(0,6,102,0.06)', color: '#000666' }}
                          >
                            {ENTITY_LABELS[log.entityType] || log.entityType}
                          </span>
                          {log.entityLabel && (
                            <div className="text-xs text-gray-500 truncate max-w-[140px]">{log.entityLabel}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>

                    {/* Metadata */}
                    <td className="px-4 py-3.5">
                      <MetadataPreview metadata={log.metadata} />
                    </td>

                    {/* IP */}
                    <td className="px-4 py-3.5">
                      <span className="text-xs text-gray-400 font-mono">{log.ipAddress || '—'}</span>
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3.5">
                      <div>
                        <span className="text-xs text-gray-500">{formatDateAr(log.createdAt)}</span>
                        <div className="text-xs text-gray-400">{formatRelativeTime(log.createdAt)}</div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {meta.totalPages > 1 && (
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderTop: '1px solid #e5e7eb' }}
          >
            <span className="text-xs text-gray-500">
              {meta.total} سجل — صفحة {meta.page} من {meta.totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(meta.totalPages, p + 1))}
                disabled={page === meta.totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
